/**
 * Models for the Bookmarks (Reading List) feature
 *
 * @remarks
 * Represents user bookmarks on blog posts, scoped to an organization.
 * The bookmark list endpoint returns paginated results with populated post data.
 *
 * @see BOOKMARKS_ANGULAR_SERVICE.md for the full API reference
 */

import { PaginationMeta } from './api-response.model';
import { PostTypeRef } from './base';

// ---------------------------------------------------------------------------
// Embedded reference types
// ---------------------------------------------------------------------------

/**
 * Minimal author reference returned inside a bookmarked post.
 * Subset of UserRef — only name is guaranteed by this endpoint.
 */
export interface BookmarkAuthorRef {
  _id: string;
  name: string;
}

// ---------------------------------------------------------------------------
// Core models
// ---------------------------------------------------------------------------

/**
 * Populated blog post snapshot embedded in a bookmark document.
 * Only the fields required for a reading-list card are projected by the API.
 */
export interface BookmarkedPost {
  _id: string;
  title: string;
  slug: string;
  excerpt?: string;
  coverImageUrl?: string;
  ogImage?: string;
  createdAt: string;
  /** Null when the author account has been deleted */
  authorId: BookmarkAuthorRef | null;
  /** Null when the post type has been deleted */
  postTypeId: PostTypeRef | null;
}

/**
 * A single bookmark document as returned by GET /api/bookmarks
 */
export interface Bookmark {
  _id: string;
  orgId: string;
  userId: string;
  /** The post is always populated in list responses */
  postId: BookmarkedPost;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Request / response shapes
// ---------------------------------------------------------------------------

/**
 * Request body for POST /api/bookmarks
 */
export interface AddBookmarkRequest {
  postId: string;
  orgId: string;
}

/**
 * Response body for add / remove bookmark actions
 */
export interface BookmarkActionResponse {
  success: boolean;
  message: string;
  data?: { postId: string; orgId: string };
}

/**
 * Paginated response for GET /api/bookmarks
 */
export interface PaginatedBookmarks {
  success: boolean;
  count: number;
  data: Bookmark[];
  pagination: PaginationMeta;
}

// ---------------------------------------------------------------------------
// Signal state type
// ---------------------------------------------------------------------------

/**
 * Internal reactive state shape for BookmarkService
 */
export interface BookmarksState {
  bookmarks: Bookmark[];
  /** Fast O(1) look-up for `isBookmarked()` checks */
  bookmarkedPostIds: Set<string>;
  pagination: PaginationMeta | null;
  loading: boolean;
  error: string | null;
}
